import '../App.css';
import CreateApi from '../shared/Settings/appSettings';
import {
    Box, Stack, Typography, Divider,
    CircularProgress, Popover, Button, IconButton, TextField
} from '@mui/material';
import React, { useState, useEffect } from 'react';

import textLogoSouth from '../assets/text-logo-linear-south.png'
import { sizeAdjust } from '../shared/Settings/SizeStyles';
import TournamentsManager from './Components/TournamentManager';
import { useNavigate } from 'react-router-dom';
import { SidebarBrandingBlock } from './Components/TournamentBuilderHeader';
import { FetchStaticDataOptions } from './Components/Functions/GetStaticData';
import { useTBContext } from './Contexts/useTBStates';
import { useAuth } from 'react-oidc-context';
import MenuSpanHeader from '../shared/Components/MenuSpanHeader';
import { GroupsManager } from './Components/GroupsManager';
import { UserManagement } from './Components/User Management/UserManagement';
import { ImageLibraryContent } from '../shared/Components/ImageLibrary';
import { setAppTheme } from '../shared/Settings/appTheme';
import { NavigationHistoryBar } from './Components/NavigationHistoryBar';
import { TabChipSelectorsBar } from './Components/TabChipSelectorsBar';
import { CommandCenter } from './Components/CommandCenter/CommandCenter';
import { APP_VERSION } from '../shared/config/appVersion';


interface TournamentBuilderProps {
    props: any;
}

const paddingWidth = sizeAdjust(1).paddingWidth;

const getInitialAppDims = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
  appWidth: window.innerWidth,
  appHeight: window.innerHeight - paddingWidth,
  paddingWidth: paddingWidth
});

const getInitialCompHeights = () => ({
  appBarHeight: 0,
  appFooterHeight: 0,
  appUsableHeight: 0,
  appUsableWidth: 0,
  extraHeight: 0
});

const SIDEBAR_WIDTH = 200;

interface SidebarAccountPopoverProps {
    open: boolean;
    onClose: () => void;
    anchorEl: HTMLElement | null;
    factor: number;
    setFactor: (v: number) => void;
    setTheme: (v: ReturnType<typeof setAppTheme>) => void;
}

const SidebarAccountPopover = ({ open, onClose, anchorEl, factor, setFactor, setTheme }: SidebarAccountPopoverProps) => {
    const auth = useAuth();
    const [zoom, setZoom] = useState(Math.round(factor * 100));

    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN ?? '';
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID ?? '';

    const handleSignOut = () => {
        auth.signoutSilent();
        window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(window.location.origin)}`;
    };

    const handlePasskey = () => {
        window.location.href = `${cognitoDomain}/passkeys/add?client_id=${clientId}&redirect_uri=${encodeURIComponent(window.location.origin)}`;
    };

    const applyZoom = (val: number) => {
        setZoom(val);
        setFactor(val / 100);
        setTheme(setAppTheme(val / 100));
    };

    return (
        <Popover
            open={open}
            onClose={onClose}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
            <Box sx={{ padding: 1, minHeight: 100, minWidth: 250 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 500, width: '100%', textAlign: 'center' }}>Settings</Typography>
                <Divider />
                <Stack direction='column' spacing={1} padding={2}>
                    <Typography variant='caption'>My Account</Typography>
                    <Typography variant='caption'>Subscription: Personal - Free</Typography>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography variant='body2' sx={{ width: '50%' }}>Page Zoom</Typography>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <IconButton sx={{ borderRadius: 1, height: 25 }} onClick={() => applyZoom(zoom - 5)}>
                                <Typography variant='body1'>-</Typography>
                            </IconButton>
                            <TextField
                                type='number'
                                variant='outlined'
                                size='small'
                                inputProps={{ style: { textAlign: 'center', padding: '4px', fontSize: 14, width: 40 }, onFocus: (e) => e.target.select() }}
                                value={zoom}
                                onChange={(e) => setZoom(Number(e.target.value.replace(/^0+/, '')))}
                                onBlur={(e) => applyZoom(Number(e.target.value))}
                                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                            />
                            <IconButton sx={{ borderRadius: 1, height: 25 }} onClick={() => applyZoom(zoom + 5)}>
                                <Typography variant='body1'>+</Typography>
                            </IconButton>
                        </Stack>
                    </Stack>
                    <Divider />
                    <Button variant='text' sx={{ color: 'primary.main', padding: 0, justifyContent: 'flex-start' }} onClick={handlePasskey}>
                        <Typography variant='caption'>Set up passkey</Typography>
                    </Button>
                    <Box sx={{ minHeight: 10 }} />
                    <Divider />
                    <Button variant='text' sx={{ color: 'primary.main', padding: 0, justifyContent: 'flex-start' }} onClick={handleSignOut}>
                        <Typography variant='caption'>Sign Out</Typography>
                    </Button>
                </Stack>
            </Box>
        </Popover>
    );
};

const calculateDimensions = (
    isLandscape: boolean,
    navBarHeight: number,
    chipBarHeight: number,
    appFooterHeight: number
) => {
  const totalTopBars = navBarHeight + chipBarHeight;
  if (isLandscape && window.innerHeight < 900) {
    return {
      appDims: {
        width: window.innerWidth,
        height: window.innerHeight - paddingWidth,
        appWidth: window.innerWidth,
        appHeight: window.innerHeight - paddingWidth,
        paddingWidth: paddingWidth
      },
      compHeights: {
        appBarHeight: totalTopBars,
        appFooterHeight,
        appUsableHeight: window.innerHeight - totalTopBars - appFooterHeight,
        appUsableWidth: window.innerWidth - SIDEBAR_WIDTH,
        extraHeight: 80
      }
    };
  }

  return {
    appDims: {
      width: window.innerWidth,
      height: window.innerHeight - paddingWidth,
      appWidth: window.innerWidth,
      appHeight: window.innerHeight - paddingWidth,
      paddingWidth: paddingWidth
    },
    compHeights: {
      appBarHeight: totalTopBars,
      appFooterHeight,
      appUsableHeight: window.innerHeight - totalTopBars - appFooterHeight,
      appUsableWidth: window.innerWidth - SIDEBAR_WIDTH,
      extraHeight: 0
    }
  };
};

export const TournamentBuilder = ({ props }: TournamentBuilderProps) => {

    const api = CreateApi();
    const navigate = useNavigate();
    const auth = useAuth();

    const {userData} = props;

    const {
        appSection, viewPanel,
        factor, appTheme,
        viewEvent,
        viewGroup,
        setOptionsLoaded,
        setUserData,
        setFactor,
        setTheme,
    } = useTBContext();

    const [compHeights, setCompHeights] = useState(getInitialCompHeights());
    const [appDims, setAppDims] = useState(getInitialAppDims());
    const [isLoading, setIsLoading] = useState(true);
    const [firstRender, setFirstRender] = useState(true);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);

    const getAppDims = React.useCallback(() => {
      const navBar = document.getElementById('tournament-builder-nav-bar');
      const chipBar = document.getElementById('tournament-builder-chip-bar');
      const appFooter = document.getElementById('tournament-builder-footer');
      const navBarHeight = navBar ? navBar.offsetHeight : 40;
      const chipBarHeight = chipBar ? chipBar.offsetHeight : 40;
      const appFooterHeight = appFooter ? appFooter.offsetHeight : 0;
      const isLandscape = window.innerWidth > window.innerHeight;

      const { appDims: newAppDims, compHeights: newCompHeights } = calculateDimensions(
          isLandscape, navBarHeight, chipBarHeight, appFooterHeight
      );

      setAppDims(newAppDims);
      setCompHeights(newCompHeights);
    }, []);

    useEffect(() => {
        const timeOutDelay = setTimeout(() => {
            setFirstRender(false);
        }, 500);

        FetchStaticDataOptions(api)
        .then((options) => {
            setOptionsLoaded(options);
        })
        .catch((error) => {
            console.error('Error fetching static data options:', error);
        });

        window.addEventListener('resize', getAppDims);

        if (userData) {
            setUserData(userData);
        }

        return () => {
            window.removeEventListener('resize', getAppDims);
            clearTimeout(timeOutDelay);
        };
    }, [getAppDims, setOptionsLoaded]);

    useEffect(() => {
        if (!firstRender) {
            setIsLoading(false);
        }
    }, [firstRender]);

    useEffect(() => {
        getAppDims();
    }, [isLoading, factor]);

    useEffect(() => {
        if (userData) {
            setUserData(userData);
        }
    }, [userData, setUserData]);

    const scale = factor < 1 ? factor : 1;

    return (
        <Stack
            id='tournament-builder-container'
            direction='column'
            sx={{
                width: '100dvw',
                height: '100dvh',
                overflow: 'hidden',
                overscrollBehavior: 'none',
                WebkitOverscrollBehavior: 'none',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y',
            }}
        >
            {/* ── BODY ROW (sidebar + content) ── */}
            <Stack
                id='tournament-builder-body'
                direction='row'
                sx={{ flex: 1, overflow: 'hidden' }}
            >
                {/* ── SIDEBAR ── */}
                <Box
                    id='tournament-builder-drawer'
                    sx={{
                        width: SIDEBAR_WIDTH * scale,
                        flexShrink: 0,
                        display: firstRender ? 'none' : 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'sepia.background',
                        borderRight: '1px solid',
                        borderColor: 'sepia.extraLight',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehavior: 'contain',
                    }}
                >
                    {/* Branding block: flag + script logos */}
                    <SidebarBrandingBlock factor={factor} />

                    {/* Main nav items */}
                    <Stack direction='column' spacing={2} sx={{ mt: 1.5, px: 0.5 }}>
                        <MenuSpanHeader
                            matchName='tournaments'
                            label='Overview'
                            defaultPanel='snapshot'
                            panelMatch='snapshot'
                        />
                        <MenuSpanHeader
                            matchName='tournaments'
                            label='Tournaments'
                            defaultPanel='eventList'
                            panelMatch='eventList'
                        />
                        <MenuSpanHeader
                            matchName='groups'
                            label='Series / Tours'
                            defaultPanel='groupList'
                            groupFilter='series'
                        />
                        <MenuSpanHeader
                            matchName='groups'
                            label='Leagues'
                            defaultPanel='groupList'
                            groupFilter='league'
                        />
                        <MenuSpanHeader
                            matchName='groups'
                            label='Trips'
                            defaultPanel='groupList'
                            groupFilter='trip'
                        />
                        <MenuSpanHeader
                            matchName='admin'
                            label='Traditions'
                            defaultPanel='traditions'
                            panelMatch='traditions'
                        />
                        <MenuSpanHeader
                            matchName='admin'
                            label='Admin Console'
                            defaultPanel='imageLibrary'
                            panelMatch='imageLibrary'
                        />
                        <MenuSpanHeader
                            matchName='commandCenter'
                            label='Command Center'
                            defaultPanel='attestScores'
                        />
                    </Stack>

                    {/* Spacer pushes User Management to bottom */}
                    <Box sx={{ flex: 1 }} />

                    <Stack direction='column' spacing={0.5} sx={{ mb: 1, px: 0.5, mt: 0.5 }}>
                        <MenuSpanHeader
                            matchName='userManagement'
                            label='User Management'
                            defaultPanel='userProfile'
                        />
                    </Stack>

                    {/* User info / avatar */}
                    <Divider sx={{ borderColor: 'sepia.extraLight' }} />
                    <Box
                        id='sidebar-account-trigger'
                        sx={{
                            px: 2,
                            py: 1.25 * scale,
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                        }}
                        onClick={() => setAccountMenuOpen(true)}
                    >
                        <Stack direction='row' spacing={1.5} alignItems='center'>
                            <Box
                                sx={{
                                    width: 40 * scale,
                                    height: 40 * scale,
                                    borderRadius: '50%',
                                    backgroundColor: 'sepia.extraLight',
                                    border: `1px solid ${appTheme.palette.sepia?.main}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Typography
                                    variant='subtitle2'
                                    sx={{ fontWeight: 600, color: 'sepia.dark', fontSize: `${14 * scale}px` }}
                                >
                                    {auth.user?.profile.given_name?.[0]}{auth.user?.profile.family_name?.[0]}
                                </Typography>
                            </Box>
                            <Stack direction='column' spacing={0} sx={{ overflow: 'hidden', minWidth: 0 }}>
                                <Typography
                                    variant='caption'
                                    sx={{
                                        color: 'sepia.dark',
                                        fontSize: `${12 * scale}px`,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {auth.user?.profile.email as string ?? ''}
                                </Typography>
                                <Typography
                                    variant='caption'
                                    sx={{ color: 'sepia.main', fontSize: `${11 * scale}px` }}
                                >
                                    My Account
                                </Typography>
                            </Stack>
                        </Stack>
                    </Box>

                    {/* Account settings popover */}
                    <SidebarAccountPopover
                        open={accountMenuOpen}
                        onClose={() => setAccountMenuOpen(false)}
                        anchorEl={document.getElementById('sidebar-account-trigger')}
                        factor={factor}
                        setFactor={setFactor}
                        setTheme={setTheme}
                    />
                </Box>

                {/* ── RIGHT CONTENT COLUMN ── */}
                <Stack
                    direction='column'
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        backgroundColor: 'sepia.background',
                    }}
                >
                    {/* Navigation History breadcrumb bar */}
                    <NavigationHistoryBar />

                    {/* Tab Chip Selectors bar */}
                    <TabChipSelectorsBar />

                    {/* Main content area */}
                    <Stack
                        id='tournament-builder-content'
                        direction='column'
                        sx={{
                            flex: 1,
                            overflow: 'hidden',
                            alignItems: 'center',
                            backgroundColor: 'sepia.background',
                            WebkitOverflowScrolling: 'touch',
                            overscrollBehavior: 'contain',
                        }}
                    >
                        {/* Loading state */}
                        <Box
                            id='tournament-builder-loading-box'
                            sx={{
                                display: isLoading && firstRender ? 'flex' : 'none',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                width: '100%',
                                transition: 'opacity 0.5s ease-in-out',
                                opacity: isLoading && firstRender ? 1 : 0,
                                pointerEvents: isLoading && firstRender ? 'auto' : 'none',
                            }}
                            className={isLoading && firstRender ? 'loading-transition' : 'loading-hidden'}
                        >
                            <Typography variant='h6'>Loading...</Typography>
                            <CircularProgress />
                        </Box>

                        {/* Tournaments: Overview / Event List */}
                        {appSection === 'tournaments' && !isLoading && viewPanel === 'snapshot' && (
                            <Box sx={{ width: '100%', height: '100%', p: 3, overflow: 'auto' }}>
                                <Typography variant='h5' color='sepia.main' sx={{ fontWeight: 600, mb: 2 }}>
                                    Overview
                                </Typography>
                                <Typography variant='body1' color='sepia.light'>
                                    Tournament snapshot coming soon.
                                </Typography>
                            </Box>
                        )}
                        {appSection === 'tournaments' && !isLoading && viewPanel === 'eventList' && (
                            <Box sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <TournamentsManager appDims={appDims} compHeights={compHeights} />
                            </Box>
                        )}
                        {appSection === 'tournaments' && !isLoading && viewEvent && viewPanel !== 'snapshot' && viewPanel !== 'eventList' && (
                            <Box sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <TournamentsManager appDims={appDims} compHeights={compHeights} />
                            </Box>
                        )}

                        {/* Groups / Collections — keep event editor nested under the collection */}
                        {appSection === 'groups' && !isLoading && viewEvent && (
                            <Box sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <TournamentsManager appDims={appDims} compHeights={compHeights} />
                            </Box>
                        )}
                        {appSection === 'groups' && !isLoading && !viewEvent && (
                            <GroupsManager appDims={appDims} compHeights={compHeights} />
                        )}

                        {/* Admin */}
                        {appSection === 'admin' && !isLoading && viewPanel === 'imageLibrary' && (
                            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflow: 'hidden', width: '100%' }}>
                                <Typography variant='h6'>Image Library</Typography>
                                <ImageLibraryContent mode='edit' />
                            </Box>
                        )}
                        {appSection === 'admin' && !isLoading && viewPanel === 'traditions' && (
                            <Box sx={{ width: '100%', height: '100%', p: 3, overflow: 'auto' }}>
                                <Typography variant='h5' color='sepia.main' sx={{ fontWeight: 600, mb: 2 }}>
                                    Traditions
                                </Typography>
                                <Typography variant='body1' color='sepia.light'>
                                    Admin Traditions coming soon.
                                </Typography>
                            </Box>
                        )}

                        {/* User Management */}
                        {appSection === 'userManagement' && !isLoading && (
                            <UserManagement appDims={appDims} compHeights={compHeights} />
                        )}

                        {/* Command Center */}
                        {appSection === 'commandCenter' && !isLoading && (
                            <CommandCenter appDims={appDims} compHeights={compHeights} />
                        )}
                    </Stack>
                </Stack>
            </Stack>

            {/* ── FOOTER ── */}
            <Box
                id='tournament-builder-footer'
                sx={{
                    top: 'auto',
                    bottom: 0,
                    backgroundColor: 'sepia.extraLight',
                    padding: 1 * scale,
                    paddingRight: 3,
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    borderTop: `1px solid ${appTheme.palette.sepia?.light}`,
                    flexShrink: 0,
                }}
            >
                <Stack direction='row' width='100%' spacing={1} alignItems='center' justifyContent='space-between' paddingLeft={1} marginRight={5}>
                    <Typography variant='body2' color='sepia.main' sx={{ fontStyle: 'italic' }}>
                        {APP_VERSION}
                    </Typography>
                    <Typography variant='body2' color='sepia.main' sx={{ fontWeight: 600 }}>
                        For Every Moment. For Every Golfer.
                    </Typography>
                </Stack>
            </Box>
        </Stack>
    );
};

export default TournamentBuilder;
